import { usePersistentState } from './core/usePersistentState.ts';
import { AccountSelect } from './core/components/AccountSelect.tsx';
// App.tsx — головна оболонка з sidebar навігацією та роутингом

import React, { useState } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { AccountsPage } from './modules/social-accounts/AccountsPage.tsx';
import { AdAccountsTab } from './modules/ad-assets/AdAccountsTab.tsx';
import { PagesTab } from './modules/ad-assets/PagesTab.tsx';
import { PostsTab } from './modules/ad-assets/PostsTab.tsx';
import { TemplatesPage } from './modules/campaign-launcher/TemplatesPage.tsx';
import { LaunchWizard } from './modules/campaign-launcher/LaunchWizard.tsx';
import { QueuePage } from './modules/campaign-launcher/QueuePage.tsx';
import { PostsMonitorPage } from './modules/comment-monitor/PostsMonitorPage.tsx';
import { useAccounts } from './modules/social-accounts/useAccounts.ts';
import { DebugDrawer } from './core/components/DebugDrawer.tsx';
import { AdsStatsPage } from './modules/ads-stats/AdsStatsPage.tsx';
import { CurrencyRatesPage } from './modules/currency-rates/CurrencyRatesPage.tsx';

interface NavItemDef {
  to: string;
  icon: string;
  label: string;
  disabled?: boolean;
}

const DOMAIN_1_NAV: NavItemDef[] = [
  { to: '/', icon: '', label: 'Акаунти' },
];

const DOMAIN_2_NAV: NavItemDef[] = [
  { to: '/ad-accounts', icon: '', label: 'Рекл. кабінети' },
  { to: '/pages', icon: '', label: 'Fan Pages' },
  { to: '/posts', icon: '', label: 'Пости' },
];

const DOMAIN_3_NAV: NavItemDef[] = [
  { to: '/templates', icon: '', label: 'Templates' },
  { to: '/launch', icon: '', label: 'Launch' },
  { to: '/launch-queue', icon: '', label: 'Launch queue' },
];

const DOMAIN_4_NAV: NavItemDef[] = [
  { to: '/comment-monitor', icon: '', label: 'Моніторинг комент.' },
];

const DOMAIN_5_NAV: NavItemDef[] = [
  { to: '/ads-stats', icon: '', label: 'По оголошеннях' },
  { to: '/currency-rates', icon: '', label: 'Курси валют' },
];

function SidebarNavItem({ item }: { item: NavItemDef }) {
  if (item.disabled) {
    return (
      <div
        className="nav-item"
        style={{ opacity: 0.4, cursor: 'not-allowed' }}
        title="Буде доступно у наступних доменах"
      >
        <span className="nav-item__icon">{item.icon}</span>
        <span>{item.label}</span>
        <span className="nav-item__badge" style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-subtle)', fontSize: '0.625rem' }}>
          незабаром
        </span>
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        `nav-item${isActive ? ' nav-item--active' : ''}`
      }
    >
      <span className="nav-item__icon">{item.icon}</span>
      <span>{item.label}</span>
    </NavLink>
  );
}

export function App() {
  const { accounts } = useAccounts();
  const [selectedAccountId, setSelectedAccountId] = usePersistentState<string>('posts.account', '');
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const filterTextMap: Record<string, string> = {
    '/pages': '/me/accounts|/me/businesses|/owned_pages|/client_pages|/published_posts|/feed|/photos',
    '/posts': 'published_posts',
    '/ad-accounts': 'adaccounts',
    '/comment-monitor': 'comments',
    '/launch': 'act_',
  };
  const currentFilter = filterTextMap[location.pathname] || '';



  // Автоматично вибрати перший акаунт коли список завантажується
  const effectiveAccountId = (accounts.some((account) => account.id === selectedAccountId) ? selectedAccountId : accounts[0]?.id) || '';
  const effectiveAccount = accounts.find((a) => a.id === effectiveAccountId) ?? null;

  return (
    <div className="app-layout">
      {/* ── Sidebar ─────────────────────────────────────── */}
      <aside className="sidebar" role="navigation" aria-label="Головне меню">
        {/* Logo */}
        <div className="sidebar__logo">
          <div>
            <div className="sidebar__logo-text">Facebook CRM</div>
            <div className="sidebar__logo-sub">Meta Management Tool</div>
          </div>
        </div>

        {/* Nav */}
        {accounts.some(account => account.temporaryTokenSetAt) && <NavLink to="/" className="notification" style={{ color: 'var(--color-warning)', margin: 'var(--space-3)' }}>
          <span role="status" aria-atomic="true">Тимчасові токени: {accounts.filter(account => account.temporaryTokenSetAt).map(account => account.name ?? account.id).join(', ')}. Після запуску поверніть постійні токени в картках акаунтів.</span>
        </NavLink>}
        <nav className="sidebar__nav">
          <div className="nav-section">Домен 1</div>
          {DOMAIN_1_NAV.map((item) => (
            <SidebarNavItem key={item.to} item={item} />
          ))}

          <div className="nav-section" style={{ marginTop: 'var(--space-4)' }}>Домен 2 — Ad Assets</div>
          {DOMAIN_2_NAV.map((item) => (
            <SidebarNavItem key={item.to} item={item} />
          ))}

          <div className="nav-section" style={{ marginTop: 'var(--space-4)' }}>Домен 3</div>
          {DOMAIN_3_NAV.map((item) => (
            <SidebarNavItem key={item.to} item={item} />
          ))}

          <div className="nav-section" style={{ marginTop: 'var(--space-4)' }}>Домен 4</div>
          {DOMAIN_4_NAV.map((item) => (
            <SidebarNavItem key={item.to} item={item} />
          ))}

          <div className="nav-section" style={{ marginTop: 'var(--space-4)' }}>Домен 5 — Ads Stats</div>
          {DOMAIN_5_NAV.map((item) => (
            <SidebarNavItem key={item.to} item={item} />
          ))}
        </nav>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--space-4) var(--space-6)',
            borderTop: '1px solid var(--color-border)',
            marginTop: 'auto',
          }}
        >
          <div className="text-xs text-subtle">
            v1.0.0 · Cloudflare Worker
          </div>
        </div>
      </aside>

      {/* ── Main content ─────────────────────────────────── */}
      <main className="main-content" role="main">
        <Routes>
          <Route path="/" element={<AccountsPage />} />

          {/* Домен 2: перед кожним табом показуємо selector акаунту */}
          <Route
            path="/ad-accounts"
            element={
              <div className="page-body">
                <div style={{ marginBottom: 'var(--space-5)' }}>
                  <h1 style={{ fontSize: '1.5rem', marginBottom: 'var(--space-1)' }}>
                    Рекламні кабінети
                  </h1>
                  <p className="text-muted text-sm">Рекламні акаунти Facebook (Ad Manager)</p>
                </div>
                {accounts.length === 0 ? (
                  <div className="glass-card empty-state">
                    <div className="empty-state__title">Немає підключених акаунтів</div>
                    <div className="empty-state__desc">
                      Спочатку підключи Facebook акаунт у розділі «Акаунти».
                    </div>
                  </div>
                ) : (
                  <>
                    <AdAccountsTab socialAccounts={accounts} />
                  </>
                )}
              </div>
            }
          />

          <Route
            path="/pages"
            element={
              <div className="page-body">
                <div style={{ marginBottom: 'var(--space-5)' }}>
                  <h1 style={{ fontSize: '1.5rem', marginBottom: 'var(--space-1)' }}>Fan Pages</h1>
                  <p className="text-muted text-sm">Підключені Facebook сторінки</p>
                </div>
                {accounts.length === 0 ? (
                  <div className="glass-card empty-state">
                    <div className="empty-state__title">Немає підключених акаунтів</div>
                    <div className="empty-state__desc">
                      Спочатку підключи Facebook акаунт у розділі «Акаунти».
                    </div>
                  </div>
                ) : (
                  <>
                    <PagesTab socialAccounts={accounts} />
                  </>
                )}
              </div>
            }
          />

          <Route
            path="/posts"
            element={
              <div className="page-body">
                <div style={{ marginBottom: 'var(--space-5)' }}>
                  <h1 style={{ fontSize: '1.5rem', marginBottom: 'var(--space-1)' }}>Пости</h1>
                  <p className="text-muted text-sm">Опубліковані пости на Facebook сторінках</p>
                </div>
                {accounts.length === 0 ? (
                  <div className="glass-card empty-state">
                    <div className="empty-state__title">Немає підключених акаунтів</div>
                    <div className="empty-state__desc">
                      Спочатку підключи Facebook акаунт у розділі «Акаунти».
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="glass-card posts-account-filter" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
                      <AccountSelect multiple={false} accounts={accounts} value={effectiveAccountId ? [effectiveAccountId] : []} onChange={(ids) => setSelectedAccountId(ids[0] ?? '')} />
                    </div>
                    <PostsTab
                      key={effectiveAccountId}
                      socialAccountId={effectiveAccountId}
                      accountName={effectiveAccount?.name}
                    />
                  </>
                )}
              </div>
            }
          />

          <Route path="/comment-monitor" element={<PostsMonitorPage />} />
          <Route path="/ads-stats" element={<AdsStatsPage />} />
          <Route path="/currency-rates" element={<CurrencyRatesPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/launch" element={<LaunchWizard />} />
          <Route path="/launch-queue" element={<QueuePage />} />
          <Route
            path="*"
            element={
              <div className="page-body empty-state">
                <div className="empty-state__title">Сторінку не знайдено</div>
              </div>
            }
          />
        </Routes>
      </main>

      {/* Floating Debug Button */}
      <button
        className="btn btn--secondary"
        style={{
          position: 'fixed',
          bottom: 'var(--space-4)',
          right: 'var(--space-4)',
          zIndex: 900,
          borderRadius: '50%',
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          fontSize: '1.25rem',
          padding: 0
        }}
        onClick={() => setIsDrawerOpen(true)}
        title="API Logs"
      >
        <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      </button>

      <DebugDrawer
        source={location.pathname==='/ads-stats'?'ads-stats':undefined}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        filterText={currentFilter}
      />
    </div>
  );
}
